import React, { useState } from 'react'
import SupportModal from '../../components/support/SupportModal'
import { handleGetRequest } from '../../utils/api-handler.util'
import { useQuery } from '@tanstack/react-query'
import { QueryKeys } from '../../utils/queryKeyGenerator'
import { useAuth } from '../../contexts/AuthContext'
import { formatTime } from '../../utils/date.util'
import { useNavigate } from 'react-router-dom'
import PageTitle from '../../components/common/PageTitle'
import {
  ChatBubbleLeftIcon,
  PlusIcon,
  ClockIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/solid'
import { MESSAGE_LISTING_REFETCH_INTERVAL_MILLIS } from '../../constants/messagingConstants'
import SupportTicketStatus from '../../components/admin/SupportTicketStatus'
import SeoHelmet from '../../components/common/SeoHelmet'
import { useFilters } from '../../hooks/useFilters'

const SupportPage = () => {
  const { currentUser } = useAuth()
  const navigate = useNavigate()
  const { filters, renderPagination } = useFilters()

  const { data: tickets } = useQuery({
    queryKey: QueryKeys.supportTickets({ page: filters.page }),
    queryFn: () =>
      handleGetRequest(`/${currentUser.userType}s/support-tickets`, {
        page: filters.page,
      }),
    refetchInterval: MESSAGE_LISTING_REFETCH_INTERVAL_MILLIS,
  })
  const ticketList = tickets?.data || []

  const [modalOpen, setModalOpen] = useState(false)

  return (
    <div className="main-container mt-2">
      <SeoHelmet
        title="Support Center"
        description="Get help and support for your Sokooni marketplace experience. Submit tickets, track requests, and connect with our support team."
        keywords="customer support, help center, support tickets, Sokooni support, marketplace help"
        url="/support"
      />
      <PageTitle>Support Center</PageTitle>
      {ticketList.length > 0 && (
        <div className="bg-gradient-to-r from-tertiary-700 to-tertiary-600 rounded-xl p-6 mb-8 text-white">
          <div className="flex flex-col gap-4">
            <div>
              <p className="text-white/90">
                Manage your support requests and get help when you need it
              </p>
            </div>
            <button
              className="bg-background text-tertiary-600 px-6 py-3 rounded-lg font-semibold hover:bg-neutral-200-50 transition-colors flex items-center gap-2 shadow-lg w-max "
              onClick={() => setModalOpen(true)}
            >
              <PlusIcon className="h-5 w-5" />
              Request Support
            </button>
          </div>
        </div>
      )}

      <div className="space-y-6">
        {ticketList.length === 0 ? (
          <div className="bg-background rounded-xl p-12 text-center shadow-sm border border-gray-100">
            <div className="bg-neutral-100 rounded-full w-20 h-20 mx-auto mb-6 flex items-center justify-center">
              <ChatBubbleLeftIcon className="h-10 w-10 text-secondary-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No support requests yet</h3>
            <p className="text-gray-500 mb-6">
              You haven't submitted any support requests. Need help? Create your first ticket.
            </p>
            <button
              className="bg-secondary-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-secondary-600 transition-colors inline-flex items-center gap-2"
              onClick={() => setModalOpen(true)}
            >
              <PlusIcon className="h-5 w-5" />
              Create Support Ticket
            </button>
          </div>
        ) : (
          <div className="grid gap-4">
            {ticketList.map((ticket) => (
              <div
                key={ticket.id}
                className="bg-background rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-all duration-200 cursor-pointer group hover:border-secondary-600/20"
                onClick={() => navigate(`/support-tickets/${ticket.id}/message`)}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-lg text-gray-900 group-hover:text-secondary-600 transition-colors truncate mb-2">
                      {ticket.subject}
                    </h3>
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <div className="flex items-center gap-1">
                        <span className="font-medium">Ticket #{ticket.id}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <ClockIcon className="h-4 w-4" />
                        <span>{formatTime(ticket.latestTimestamp)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="ml-4 flex items-center gap-3">
                    <SupportTicketStatus status={ticket.status} />
                    <ChevronRightIcon className="h-5 w-5 text-gray-400 group-hover:text-secondary-600 transition-colors" />
                  </div>
                </div>

                <div className="flex items-center justify-between pt-2 border-t border-gray-50">
                  <div className="text-sm text-gray-500">Click to view conversation</div>
                  <div className="text-xs text-gray-400 bg-neutral-200-50 px-2 py-1 rounded">
                    Last updated: {formatTime(ticket.latestTimestamp)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {renderPagination(tickets?.meta)}
      <SupportModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </div>
  )
}

export default SupportPage
